import React, { useState, useEffect, useRef, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  FloatingLabel,
  Form,
} from "react-bootstrap";
import { Edit2, Trash2, UserPlus, MapPin } from "react-feather";

import DataTable from "../../components/table";
import { useNotification } from "../../components/notification";
import { useConfirmation } from "../../contexts/confirmationContext";
import apiService from "../../services/apiService";
import { formatTime } from "../../utils";
import { useAuth } from "../../contexts/authContext";

const ManageUsers = () => {
  const didFetch = useRef(false);
  const { user } = useAuth();

  const { showNotification } = useNotification();
  const { showConfirmation } = useConfirmation();

  const [roles, setRoles] = useState([]); // List of roles
  const [users, setUsers] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingState, setLoadingState] = useState({ action: "", id: "" });

  // Consolidated filter state
  const [filters, setFilters] = useState({
    role: "",
    sortBy: "",
    sortOrder: "",
    isActive: "",
    page: { PageNumber: 1, PageSize: 25 },
    search: "",
  });

  // Fetch roles on component mount
  useEffect(() => {
    if (didFetch.current) return; // Prevent second call
    didFetch.current = true;

    const fetchRoles = async () => {
      try {
        const response = await apiService.get("/role?isDropdown=true");

        const filteredRoles =
          user?.role === "super_admin"
            ? response
            : response.filter((role) => role.value !== "super_admin");
        setRoles(filteredRoles);
      } catch (error) {
        showNotification("danger", "Failed to fetch roles.");
      }
    };
    fetchRoles();
  }, [showNotification]);

  // Fetch users on page load or when pagination/filter changes
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await apiService.get(`/users`, {
          page: filters.page.PageNumber,
          limit: filters.page.PageSize,
          role: filters.role,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          isActive: filters.isActive,
          search: filters.search,
        });

        let usersList = response.users;
        if (user?.role !== "super_admin") {
          usersList = usersList.filter((u) => u.role !== "super_admin");
        }
        setUsers(usersList);
        setTotalRecords(response.pagination.total);
      } catch (error) {
        showNotification("danger", error || "Failed to get users.", 5000);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, [filters, showNotification]);

  const handleSearchTerm = (text) => {
    setFilters((prev) => ({
      ...prev,
      search: text,
      page: { ...prev.page, PageNumber: 1 },
    }));
  };

  // Handle page change
  const handlePageChange = (pageNumber, pageSize) => {
    setFilters((prev) => ({
      ...prev,
      page: { PageNumber: pageNumber, PageSize: pageSize },
    }));
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: { ...prev.page, PageNumber: 1 },
    }));
  };

  // Handle Delete User with confirmation
  const handleDeleteUser = (userId, userName) => {
    showConfirmation({
      title: "Delete User",
      description: `Are you sure you want to delete ${userName}? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "primary",
      onConfirm: async () => {
        setLoadingState({ action: "delete", id: userId });
        try {
          await apiService.delete(`/users/${userId}`);
          handlePageChange(filters.page.PageNumber, filters.page.PageSize);
          showNotification("success", "User deleted successfully!");
        } catch (error) {
          showNotification(
            "danger",
            error?.message || "Failed to delete user."
          );
        } finally {
          setLoadingState(null);
        }
      },
    });
  };

  // Define columns
  const columns = [
    {
      accessorKey: "name",
      header: "Name",
      cell: (info) => (
        <Fragment>
          {info.row.original.firstName} {info.row.original.lastName}
        </Fragment>
      ),
    },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "phone", header: "Phone" },
    { accessorKey: "gstNumber", header: "GST Number" },
    {
      accessorKey: "role",
      header: "Role",
      cell: (info) => (
        <Badge
          bg={
            info.getValue() === "super_admin"
              ? "danger"
              : info.getValue() === "administrator"
              ? "primary"
              : "secondary"
          }
          className="text-uppercase"
        >
          {info.getValue()}
        </Badge>
      ),
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: (info) => (
        <Badge bg={info.getValue() ? "success" : "danger"}>
          {info.getValue() ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      accessorKey: "lastLogin",
      header: "Last Login",
      cell: (info) => (
        <Fragment>
          {info.getValue() ? formatTime(info.getValue()) : "Never"}
        </Fragment>
      ),
    },
  ];

  // Add actions column if user is super admin
  if (["super_admin"].includes(user?.role)) {
    columns.push({
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <div>
          <Link
            title="Edit User"
            to={`/users/edit/${info.row.original.id}`}
            className="btn btn-secondary btn-sm me-1 mb-1"
          >
            <Edit2 size={16} />
          </Link>
          <Link
            title="Add New Address"
            to={`/address/create/${info.row.original.id}`}
            className="btn btn-secondary btn-sm me-1 mb-1"
          >
            <MapPin size={16} />
          </Link>
          <Button
            variant="danger"
            size="sm"
            title="Delete User"
            onClick={() =>
              handleDeleteUser(
                info.row.original.id,
                `${info.row.original.firstName} ${info.row.original.lastName}`
              )
            }
            className="me-1 mb-1"
            disabled={
              loadingState?.action === "delete" &&
              loadingState.id === info.row.original.id
            }
          >
            {loadingState?.action === "delete" &&
            loadingState.id === info.row.original.id ? (
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            ) : (
              <Trash2 size={16} />
            )}
          </Button>
        </div>
      ),
    });
  }

  return (
    <Card className="p-4 rounded-4">
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h1 className="mb-2 fs-5">Manage Users</h1>
            <p className="mb-0 text-light-emphasis fw-light">
              View user details and manage user accounts
            </p>
          </div>
          {user?.role === "super_admin" && (
            <Button
              as={Link}
              to="/users/create"
              variant="primary"
              className="btn-main mb-3 mb-sm-0"
            >
              <UserPlus size={14} className="me-1 align-middle" />
              Add User
            </Button>
          )}
        </div>

        <hr className="my-4" />
        <Row className="my-4">
          <Col xs={12} sm={6} lg={3} className="mb-2">
            <FloatingLabel controlId="ddRoles" label="Sort By Role">
              <Form.Select
                aria-label="Sort By Role"
                value={filters.role}
                onChange={(e) => handleFilterChange("role", e.target.value)}
              >
                <option value="">--</option>
                {roles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
          </Col>
          <Col xs={12} sm={6} lg={3} className="mb-2">
            <FloatingLabel controlId="ddFields" label="Sort By Field">
              <Form.Select
                aria-label="Sort By Field"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
              >
                <option value="">--</option>
                <option value="email">Email</option>
                <option value="firstName">First name</option>
                <option value="lastName">Last name</option>
                <option value="role">Role</option>
                <option value="createdAt">Created at</option>
                <option value="updatedAt">Updated at</option>
              </Form.Select>
            </FloatingLabel>
          </Col>
          <Col xs={12} sm={6} lg={3} className="mb-2">
            <FloatingLabel controlId="ddSortOrder" label="Sort Order">
              <Form.Select
                aria-label="Sort Order"
                value={filters.sortOrder}
                onChange={(e) =>
                  handleFilterChange("sortOrder", e.target.value)
                }
              >
                <option value="">--</option>
                <option value="ASC">ASC</option>
                <option value="DESC">DESC</option>
              </Form.Select>
            </FloatingLabel>
          </Col>
          <Col xs={12} sm={6} lg={3} className="mb-2">
            <FloatingLabel controlId="ddStatus" label="Sort By Status">
              <Form.Select
                aria-label="Sort By Status"
                value={filters.isActive}
                onChange={(e) => handleFilterChange("isActive", e.target.value)}
              >
                <option value="">--</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </FloatingLabel>
          </Col>
        </Row>
        <DataTable
          columns={columns}
          data={users}
          totalRecords={totalRecords}
          page={filters.page}
          onPageChange={handlePageChange}
          loadingState={isLoading}
          searchRecords={handleSearchTerm}
          searchPlaceholder="Search users..."
        />
      </Card.Body>
    </Card>
  );
};

export default ManageUsers;
